import React from 'react'
import { FormField as FormFieldType, BetaFormData, FormErrors } from '../../types/form'

interface FormFieldProps {
  field: FormFieldType
  value: string | number | string[]
  onChange: (key: string, value: string | number | string[]) => void
  error?: string
}

const FormField: React.FC<FormFieldProps> = ({ field, value, onChange, error }) => {
  const handleStringChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(field.key, e.target.value)
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = e.target.value === '' ? '' : parseInt(e.target.value)
    onChange(field.key, numValue)
  }

  const handleArrayChange = (selectedValue: string) => {
    const currentArray = Array.isArray(value) ? value : []
    const newArray = currentArray.includes(selectedValue)
      ? currentArray.filter(item => item !== selectedValue)
      : [...currentArray, selectedValue]
    onChange(field.key, newArray)
  }

  const renderInput = () => {
    switch (field.type) {
      case 'string':
        if (field.key === 'final_pitch' || field.key === 'modding_skills' || field.key === 'source_details') {
          return (
            <textarea
              id={field.key}
              value={value as string}
              onChange={handleStringChange}
              className="w-full p-2 sm:p-3 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/50 min-h-[80px] sm:min-h-[100px] resize-y text-sm sm:text-base"
              placeholder="Type your answer here..."
              rows={3}
            />
          )
        }
        return (
          <input
            type="text"
            id={field.key}
            value={value as string}
            onChange={handleStringChange}
            className="w-full p-2 sm:p-3 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/50 text-sm sm:text-base"
            placeholder="Type your answer here..."
          />
        )

      case 'integer':
        return (
          <input
            type="number"
            id={field.key}
            value={value as number}
            onChange={handleNumberChange}
            className="w-full p-2 sm:p-3 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/50 text-sm sm:text-base"
            placeholder="Enter a number..."
            min="1"
            max="120"
          />
        )

      case 'array<string>':
        if (field.options) {
          // Multiple choice with checkboxes
          return (
            <div className="space-y-2">
              {field.options.map((option, index) => (
                <label key={index} className="flex items-start space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={Array.isArray(value) && value.includes(option)}
                    onChange={() => handleArrayChange(option)}
                    className="mt-1 w-4 h-4 text-red-600 bg-gray-800 border-gray-600 rounded focus:ring-red-500 focus:ring-2"
                  />
                  <span className="text-gray-300 group-hover:text-white transition-colors">
                    {option}
                  </span>
                </label>
              ))}
            </div>
          )
        } else {
          // Free text array (comma-separated)
          return (
            <textarea
              id={field.key}
              value={Array.isArray(value) ? value.join(', ') : ''}
              onChange={(e) => onChange(field.key, e.target.value.split(',').map(s => s.trim()).filter(s => s))}
              className="w-full p-2 sm:p-3 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/50 text-sm sm:text-base"
              placeholder="Separate multiple answers with commas..."
              rows={3}
            />
          )
        }

      default:
        return null
    }
  }

  return (
    <div className="mb-4 sm:mb-6" data-field-key={field.key}>
      <label htmlFor={field.key} className="block text-base sm:text-lg font-medium text-gray-200 mb-2 sm:mb-3">
        {field.question}
      </label>
      {renderInput()}
      {error && (
        <p className="mt-2 text-sm text-red-400">{error}</p>
      )}
    </div>
  )
}

export default FormField
